import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import { emptyObject, emptyString } from "constants/defaultValues";
import { useParams } from "hooks";

export const useAssetHierarchyRules = (hierarchyLevels, hierarchyLevelsSources, assetTypeId) => {
  const mandatoryCompletionLevel = useParams("AssetHierarchyMandatoryCompletionLevel");

  const getHierarchyLevels = () => {
    const sortedHierarchyLevels = [...hierarchyLevels].sort((a, b) => (a.level > b.level ? 1 : -1));
    return sortedHierarchyLevels.filter(z => z.level !== 0);
  };

  /**
   * Returns a boolean that indicates if a hierarchy level should be disabled
   * @param {number} level specifies the level
   * @param {boolean} hasOther specifies if otherAssetName is completed on the asset
   */
  const checkIfLevelIsDisabled = (level, hasOther) => {
    const currentLevel = hierarchyLevels.find(z => z.level === level);
    const parentLevel = level - 1;
    const previousLevel = hierarchyLevels.find(z => z.level === parentLevel);
    /* If a previous level exists and its value is not set the control of the current level is disabled */
    if (previousLevel) {
      if (!previousLevel.id) return true;
      /* Only disable the level if it has no value and otherAssetName has one (hasOther) */ else if (
        level === mandatoryCompletionLevel
      )
        return hasOther && !currentLevel.id;
      return false;
    } else if (parentLevel === 0 && !assetTypeId) return true;
  };

  const getHierarchyLevelDataSource = level => {
    return hierarchyLevelsSources[level]?.data || [];
  };

  const getHierarchyLevelDataSourceItem = (level, id) => {
    return hierarchyLevelsSources[level]?.data?.find(x => x.id === id) || emptyObject;
  };

  /**
   * Returns a boolean specifying if the level has an error
   * @param {*} level contains data for the level
   * @param {*} validation contains data for validation
   * @param {boolean} hasOther specifies if otherAssetName is completed on the asset
   */
  const getLevelError = (level, validation, hasOther) => {
    if (level.id) {
      if (hasOther && level.level === mandatoryCompletionLevel) return !isValid(validation.mandatoryLevelValue);
      else return false;
    }
    if (isValid(validation.assetHierarchyId)) {
      if (level.level < mandatoryCompletionLevel) return !isValid(validation.mandatoryLevelValue);
      else return !isValid(validation.otherAssetName);
    } else {
      if (level.level < mandatoryCompletionLevel) return true;
    }
    return false;
  };

  /**
   * Returns a string specifying the error on the level or an empty string if none
   * @param {*} level contains data for the level
   * @param {*} validation contains data for validation
   * @param {boolean} hasOther specifies if otherAssetName is completed on the asset
   */
  const getLevelHelperText = (level, validation, hasOther) => {
    if (level.id) {
      if (hasOther && level.level === mandatoryCompletionLevel) return getErrors(validation.mandatoryLevelValue);
      else return emptyString;
    }
    if (isValid(validation.assetHierarchyId)) {
      if (level.level < mandatoryCompletionLevel) return getErrors(validation.mandatoryLevelValue);
      else return getErrors(validation.otherAssetName);
    } else {
      if (level.level < mandatoryCompletionLevel) return getErrors(validation.mandatoryLevelValue);
    }
    return emptyString;
  };

  return {
    getHierarchyLevels,
    checkIfLevelIsDisabled,
    getHierarchyLevelDataSource,
    getHierarchyLevelDataSourceItem,
    getLevelError,
    getLevelHelperText
  };
};
